import React from 'react';
import PropTypes from 'prop-types';
import root from 'window-or-global';

/**
 * Fork from https://github.com/dozoisch/react-async-script
 * Usage:
 * The api is very simple makeAsyncScriptLoader(Component, scriptUrl, options). Where options can contain exposeFuncs, callbackName and globalName.
 *  Component: The component to wrap.
 *  scriptUrl: the full of the script tag.
 *  options (optional):
 *  exposeFuncs: Array of String. It'll create a function that will call the child component with the same name. It passes arguments and return value.
 *  callbackName: If the scripts calls a global function when loaded, provide the callback name here. It'll be autoregistered on the window.
 *  globalName: If wanted, provide the globalName of the loaded script. It'll be injected on the component with the same name (ex: "grecaptcha")
 *  removeOnUnmount: Boolean default=false: If set to true removes the script tag on the component unmount
 *  You can retrieve the child component using the function called getComponent().
 */
const SCRIPT_MAP = new Map();

// A counter used to generate a unique id for each component that uses the function
let idCount = 0;

/**
 * @returns {React.Component}
 */
export default function makeAsyncScript(Component, scriptURL, config) {
    const options = config || {};
    const wrappedComponentName = Component.displayName || Component.name || 'Component';

    class AsyncScriptLoader extends React.Component {
        constructor() {
            super();
            this.state = {};
        }

        asyncScriptLoaderGetScriptLoaderID() {
            if (!this.__scriptLoaderID) {
                this.__scriptLoaderID = `async-script-loader-${idCount++}`;
            }
            return this.__scriptLoaderID;
        }

        getComponent() {
            return this.childComponent;
        }

        componentDidMount() {
            const key = this.asyncScriptLoaderGetScriptLoaderID();
            const {globalName, callbackName} = options;
            if (globalName && typeof root[globalName] !== 'undefined') {
                SCRIPT_MAP.set(scriptURL, {loaded: true, observers: new Map()});
            }

            if (SCRIPT_MAP.has(scriptURL)) {
                const entry = SCRIPT_MAP.get(scriptURL);
                if (entry && (entry.loaded || entry.errored)) {
                    this.asyncScriptLoaderHandleLoad(entry);
                    return;
                }
                entry.observers.set(key, e => {
                    return this.asyncScriptLoaderHandleLoad(e);
                });
                return;
            }

            const observers = new Map();
            observers.set(key, e => {
                return this.asyncScriptLoaderHandleLoad(e);
            });
            SCRIPT_MAP.set(scriptURL, {
                loaded: false,
                observers
            });

            const script = root.document.createElement('script');

            script.src = scriptURL;
            script.async = 1;

            const callObserverFuncAndRemoveObserver = func => {
                if (SCRIPT_MAP.has(scriptURL)) {
                    const mapEntry = SCRIPT_MAP.get(scriptURL);
                    const observersMap = mapEntry.observers;

                    for (const [obsKey, observer] of observersMap) {
                        if (func(observer)) {
                            observersMap.delete(obsKey);
                        }
                    }
                }
            };

            if (callbackName && typeof window !== 'undefined') {
                root[callbackName] = AsyncScriptLoader.asyncScriptLoaderTriggerOnScriptLoaded;
            }

            script.onload = () => {
                const mapEntry = SCRIPT_MAP.get(scriptURL);
                if (mapEntry) {
                    mapEntry.loaded = true;
                    callObserverFuncAndRemoveObserver(observer => {
                        if (callbackName) {
                            return false;
                        }
                        observer(mapEntry);
                        return true;
                    });
                }
            };
            script.onerror = event => {
                const mapEntry = SCRIPT_MAP.get(scriptURL);
                if (mapEntry) {
                    mapEntry.errored = true;
                    callObserverFuncAndRemoveObserver(observer => {
                        observer(mapEntry);
                        return true;
                    });
                }
            };

            // (old) MSIE browsers may call "onreadystatechange" instead of "onload"
            script.onreadystatechange = () => {
                if (this.readyState === 'loaded') {
                    // wait for other events, then call onload if default onload hadn't been called
                    setTimeout(() => {
                        const mapEntry = SCRIPT_MAP.get(scriptURL);
                        if (mapEntry && mapEntry.loaded !== true) {
                            script.onload();
                        }
                    }, 0);
                }
            };

            root.document.body.appendChild(script);
        }

        asyncScriptLoaderHandleLoad(state) {
            this.setState(state, this.props.asyncScriptOnLoad);
        }

        componentWillUnmount() {
            // Remove tag script
            if (options.removeOnUnmount === true) {
                const allScripts = root.document.getElementsByTagName('script');
                for (let i = 0; i < allScripts.length; i += 1) {
                    if (allScripts[i].src.indexOf(scriptURL) > -1) {
                        if (allScripts[i].parentNode) {
                            allScripts[i].parentNode.removeChild(allScripts[i]);
                        }
                    }
                }
            }
            // Clean the observer entry
            const mapEntry = SCRIPT_MAP.get(scriptURL);
            if (mapEntry) {
                mapEntry.observers.delete(this.asyncScriptLoaderGetScriptLoaderID());
                if (options.removeOnUnmount === true) {
                    SCRIPT_MAP.delete(scriptURL);
                }
            }
        }

        render() {
            const {globalName} = options;
            const {...childProps} = this.props;
            if (globalName && typeof window !== 'undefined') {
                childProps[globalName] =
                    typeof root[globalName] !== 'undefined' ? root[globalName] : undefined;
            }
            return (
                <Component
                    ref={comp => {
                        this.childComponent = comp;
                    }}
                    {...childProps}
                />
            );
        }
    }
    AsyncScriptLoader.displayName = `AsyncScriptLoader(${wrappedComponentName})`;
    AsyncScriptLoader.propTypes = {
        asyncScriptOnLoad: PropTypes.func
    };
    AsyncScriptLoader.asyncScriptLoaderTriggerOnScriptLoaded = () => {
        const mapEntry = SCRIPT_MAP.get(scriptURL);
        if (!mapEntry || !mapEntry.loaded) {
            throw new Error('Script is not loaded.');
        }
        for (const observer of mapEntry.observers.values()) {
            observer(mapEntry);
        }
        delete root[options.callbackName];
    };

    if (options.exposeFuncs) {
        for (const funcToExpose of options.exposeFuncs) {
            /* eslint-disable no-loop-func */
            AsyncScriptLoader.prototype[funcToExpose] = () => {
                /* eslint-disable prefer-rest-params */
                return this.getComponent()[funcToExpose](...arguments);
            };
            /* eslint-enable no-loop-func */
        }
    }
    return AsyncScriptLoader;
}
